// Exclusive Caps — revelado al hacer scroll (una sola vez por elemento).
(() => {
  const seen = new WeakSet();
  // objetivo observado → elementos que se revelan con él
  const groups = new Map();

  const reveal = (target) => {
    (groups.get(target) || [target]).forEach((el) => el.classList.add('is-in'));
    groups.delete(target);
  };

  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          reveal(e.target);
          io.unobserve(e.target);
        }
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 })
    : null;
  // Títulos en ranura: se observa su contenedor y basta con que asome (si no, el título entra a la pantalla aún cerrado)
  const ioMask = io
    ? new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          reveal(e.target);
          ioMask.unobserve(e.target);
        }
      }, { rootMargin: '0px 0px 12% 0px', threshold: 0 })
    : null;

  const watch = (el, i) => {
    if (seen.has(el)) return;
    seen.add(el);
    if (i !== undefined) el.style.setProperty('--ec-i', i % 8);
    // Un elemento con clip-path cerrado no tiene área visible: se observa su contenedor
    const mask = el.classList.contains('ec-mask');
    const target = mask ? el.parentElement : el;
    if (!io) return el.classList.add('is-in');
    if (mask) {
      // grupo propio para que el título no espere al resto del contenedor
      ioMask.observe(target);
      const key = target;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(el);
      return;
    }
    if (!groups.has(target)) {
      groups.set(target, []);
      io.observe(target);
    }
    groups.get(target).push(el);
  };

  const scan = (root = document) => {
    root.querySelectorAll('[data-ec-reveal], .ec-reveal, .ec-mask').forEach((el) => watch(el));
    // Tarjetas de producto del tema: entrada escalonada
    root.querySelectorAll('.product-grid__item, .resource-list__item').forEach((el, i) => {
      if (seen.has(el)) return;
      el.classList.add('ec-reveal');
      watch(el, i);
    });
  };

  // "Elige tu estilo": al tocar, enfocar o pasar sobre un estilo cambia la foto fija
  const initStyles = (root = document) => {
    root.querySelectorAll('[data-ec-styles]').forEach((sec) => {
      if (sec.dataset.ecReady) return;
      sec.dataset.ecReady = '1';
      const items = [...sec.querySelectorAll('[data-ec-style]')];
      const imgs = [...sec.querySelectorAll('[data-ec-style-img]')];
      const activate = (li) => {
        items.forEach((x) => {
          const on = x === li;
          x.classList.toggle('is-active', on);
          x.querySelector('button').setAttribute('aria-expanded', on);
        });
        imgs.forEach((im) => im.classList.toggle('is-active', im.dataset.ecStyleImg === li.dataset.ecStyle));
      };
      const fine = matchMedia('(hover: hover) and (pointer: fine)');
      items.forEach((li) => {
        const btn = li.querySelector('button');
        btn.addEventListener('click', () => activate(li));
        btn.addEventListener('focus', () => activate(li));
        btn.addEventListener('pointerenter', () => { if (fine.matches) activate(li); });
      });
    });
  };

  // "Voltéala": la línea sigue al dedo 1:1; al entrar en pantalla se mueve una vez para enseñar que se arrastra
  const initFlip = (root = document) => {
    root.querySelectorAll('[data-ec-flip]').forEach((sec) => {
      if (sec.dataset.ecReady) return;
      sec.dataset.ecReady = '1';
      const range = sec.querySelector('[data-ec-flip-range]');
      const handle = sec.querySelector('.ec-flip__handle');
      const insides = [...sec.querySelectorAll('.ec-flip__in')];
      const pairs = [...sec.querySelectorAll('[data-ec-flip-pair]')];
      const picks = [...sec.querySelectorAll('[data-ec-flip-pick]')];
      const price = sec.querySelector('[data-ec-flip-price]');
      const link = sec.querySelector('[data-ec-flip-link]');
      let hint = 0;

      const set = (pos) => {
        handle.style.transform = `translateX(${pos - 50}%)`;
        insides.forEach((im) => { im.style.clipPath = `inset(0 0 0 ${pos}%)`; });
      };
      const stopHint = () => { cancelAnimationFrame(hint); hint = 0; };
      range.addEventListener('input', () => { stopHint(); set(+range.value); });
      range.addEventListener('pointerdown', stopHint);

      picks.forEach((btn) => btn.addEventListener('click', () => {
        const i = btn.dataset.ecFlipPick;
        picks.forEach((b) => {
          const on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on);
        });
        pairs.forEach((p) => p.classList.toggle('is-active', p.dataset.ecFlipPair === i));
        if (price) price.textContent = btn.dataset.price;
        if (link) link.href = btn.dataset.url;
      }));

      // Pista: 50 → 22 → 78 → 50, ease-in-out, una sola vez
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      const playHint = () => {
        const keys = [50, 22, 78, 50];
        const seg = 520;
        let t0;
        const step = (now) => {
          t0 ??= now;
          const k = Math.min((now - t0) / seg, keys.length - 1);
          const i = Math.min(Math.floor(k), keys.length - 2);
          const pos = keys[i] + (keys[i + 1] - keys[i]) * ease(k - i);
          set(pos);
          range.value = pos;
          hint = k < keys.length - 1 ? requestAnimationFrame(step) : 0;
        };
        hint = requestAnimationFrame(step);
      };
      set(+range.value);
      if (matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;
      const hio = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        hio.disconnect();
        setTimeout(playHint, 350);
      }, { threshold: 0.6 });
      hio.observe(range);
    });
  };

  // "La placa de cerca": la lupa sigue al mouse 1:1; con el dedo o el teclado se desliza al punto
  const initLupa = (root = document) => {
    root.querySelectorAll('[data-ec-lupa]').forEach((sec) => {
      if (sec.dataset.ecReady) return;
      sec.dataset.ecReady = '1';
      const stage = sec.querySelector('[data-ec-lupa-stage]');
      const lens = sec.querySelector('[data-ec-lupa-lens]');
      const pairs = [...sec.querySelectorAll('[data-ec-lupa-pair]')];
      const picks = [...sec.querySelectorAll('[data-ec-lupa-pick]')];
      const price = sec.querySelector('[data-ec-lupa-price]');
      const link = sec.querySelector('[data-ec-lupa-link]');
      const ZOOM_MAX = 2.4;
      // Nunca ampliar más de lo que la foto tiene en píxeles reales (si no, se ve borrosa)
      const zoomFor = (b) => {
        const native = +active.dataset.native || 3000;
        return Math.max(1.4, Math.min(ZOOM_MAX, native / (b.w * (window.devicePixelRatio || 1)) * 1.15));
      };
      let active = pairs[0];
      const restOf = (p) => (p.dataset.x ? { x: +p.dataset.x, y: +p.dataset.y } : null);
      let rest = restOf(active);
      let pos = rest ? { ...rest } : { x: 0.5, y: 0.45 };
      let hideTimer = 0;
      let touching = false;
      const hide = () => lens.classList.add('is-hidden');
      let loaded = false;

      // Rectángulo donde se dibuja la foto (object-fit: contain)
      const box = () => {
        const W = stage.clientWidth;
        const H = stage.clientHeight;
        const img = active.querySelector('img');
        const r = img && img.naturalWidth ? img.naturalWidth / img.naturalHeight : 1;
        const w = Math.min(W, H * r);
        const h = w / r;
        return { x: (W - w) / 2, y: (H - h) / 2, w, h };
      };
      const hotspot = sec.querySelector('[data-ec-lupa-hotspot]');
      // El punto que late va sobre la placa (o al centro en la ficha)
      const placeHotspot = () => {
        if (!hotspot) return;
        const b = box();
        const r = rest || { x: 0.5, y: 0.45 };
        // translate (no transform): el pulso anima scale y no debe escalar también la posición
        hotspot.style.translate = `${b.x + r.x * b.w}px ${b.y + r.y * b.h}px`;
        stage.classList.add('is-ready');
      };
      const render = () => {
        placeHotspot();
        const b = box();
        const L = lens.offsetWidth;
        const Z = zoomFor(b);
        const cx = b.x + pos.x * b.w;
        const cy = b.y + pos.y * b.h;
        // Con el dedo la lupa va arriba del punto para que el dedo no la tape
        let ty = cy - L / 2 - (touching ? L * 0.7 : 0);
        if (touching && ty < 0) ty = cy + L * 0.2; // en el borde de arriba va abajo del dedo
        lens.style.transform = `translate(${cx - L / 2}px, ${ty}px)`;
        lens.style.backgroundSize = `${b.w * Z}px ${b.h * Z}px`;
        lens.style.backgroundPosition = `${-(pos.x * b.w * Z - L / 2)}px ${-(pos.y * b.h * Z - L / 2)}px`;
      };
      const load = () => {
        if (loaded) return;
        loaded = true;
        lens.style.backgroundImage = `url("${active.dataset.zoom}")`;
      };
      const moveTo = (x, y, glide) => {
        load();
        if (!glide) stage.classList.add('is-used');
        lens.classList.remove('is-hidden');
        lens.classList.toggle('is-gliding', !!glide);
        pos = { x: Math.min(Math.max(x, 0), 1), y: Math.min(Math.max(y, 0), 1) };
        render();
      };
      const fromEvent = (e) => {
        const r = stage.getBoundingClientRect();
        const b = box();
        return [(e.clientX - r.left - b.x) / b.w, (e.clientY - r.top - b.y) / b.h];
      };

      stage.addEventListener('pointermove', (e) => { if (e.pointerType === 'mouse') moveTo(...fromEvent(e), false); });
      // En reposo la lupa no se ve; queda lista sobre la placa para el teclado
      const park = () => {
        if (rest) pos = { ...rest };
        hide();
      };
      stage.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') park(); });
      const endTouch = () => {
        touching = false;
        clearTimeout(hideTimer);
        hideTimer = setTimeout(() => { hide(); stage.classList.remove('is-zooming'); }, 2500);
      };
      stage.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return;
        clearTimeout(hideTimer);
        touching = true;
        stage.classList.add('is-zooming');
        try { stage.setPointerCapture(e.pointerId); } catch (_) {}
        moveTo(...fromEvent(e), false);
      });
      stage.addEventListener('pointermove', (e) => {
        if (e.pointerType === 'mouse' || !touching) return;
        moveTo(...fromEvent(e), false);
      });
      stage.addEventListener('pointerup', (e) => { if (e.pointerType !== 'mouse') endTouch(); });
      stage.addEventListener('pointercancel', (e) => { if (e.pointerType !== 'mouse') endTouch(); });
      stage.addEventListener('blur', hide);
      stage.addEventListener('keydown', (e) => {
        const step = 0.04;
        const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
        if (!d) return;
        e.preventDefault();
        moveTo(pos.x + d[0], pos.y + d[1], true);
      });
      window.addEventListener('resize', render);
      // Desde el botón de la ficha: la lupa aparece sobre el centro de la placa
      sec.addEventListener('ec-lupa:show', () => {
        setTimeout(() => {
          moveTo(0.5, 0.45, false);
          clearTimeout(hideTimer);
          hideTimer = setTimeout(hide, 3500);
        }, 600);
      });
      active.querySelector('img')?.addEventListener('load', render);

      picks.forEach((btn) => btn.addEventListener('click', () => {
        const i = btn.dataset.ecLupaPick;
        picks.forEach((b) => {
          const on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on);
        });
        pairs.forEach((p) => p.classList.toggle('is-active', p.dataset.ecLupaPair === i));
        active = pairs.find((p) => p.dataset.ecLupaPair === i);
        rest = restOf(active);
        lens.style.backgroundImage = `url("${active.dataset.zoom}")`;
        loaded = true;
        const img = active.querySelector('img');
        if (img && !img.complete) img.addEventListener('load', render, { once: true });
        park();
        if (price) price.textContent = btn.dataset.price;
        if (link) link.href = btn.dataset.url;
      }));

      render();
      if (!('IntersectionObserver' in window)) return load();
      const lio = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        lio.disconnect();
        load();
        if (!rest || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        // Pista: la lupa da una vuelta corta alrededor de la placa, una sola vez
        const path = [[-0.1, -0.04], [0.1, -0.02], [0.06, 0.08], [0, 0]];
        path.forEach(([dx, dy], k) => setTimeout(() => moveTo(rest.x + dx, rest.y + dy, true), 400 + k * 420));
        setTimeout(hide, 400 + path.length * 420 + 300);
      }, { threshold: 0.5 });
      lio.observe(stage);
    });
  };

  // Placa en 3D: la tarjeta se inclina hacia el mouse (máx. 7°) y la luz lo sigue
  const initTilt = () => {
    if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const MAX = 7;
    let raf = 0;
    document.addEventListener('pointermove', (e) => {
      const g = e.target.closest && e.target.closest('.product-card .card-gallery');
      if (!g || raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const r = g.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width;
        const y = (e.clientY - r.top) / r.height;
        g.classList.add('is-tilting');
        g.style.transform = `perspective(800px) rotateX(${(0.5 - y) * MAX * 2}deg) rotateY(${(x - 0.5) * MAX * 2}deg)`;
        g.style.setProperty('--mx', `${x * 100}%`);
        g.style.setProperty('--my', `${y * 100}%`);
      });
    }, { passive: true });
    document.addEventListener('pointerout', (e) => {
      const g = e.target.closest && e.target.closest('.product-card .card-gallery');
      if (!g || g.contains(e.relatedTarget)) return;
      g.classList.remove('is-tilting');
      g.style.transform = '';
    });
  };

  // Ficha: botón sobre la foto principal que lleva a la lupa y la abre sobre la placa
  const initLupaJump = () => {
    const lupa = document.querySelector('.ec-lupa--product');
    const media = document.querySelector('main media-gallery, main .product-information__media');
    if (!lupa || !media || media.querySelector('.ec-lupa-jump')) return;
    if (getComputedStyle(media).position === 'static') media.style.position = 'relative';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ec-lupa-jump';
    btn.textContent = lupa.dataset.jumpLabel;
    btn.addEventListener('click', () => {
      lupa.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      lupa.dispatchEvent(new CustomEvent('ec-lupa:show'));
    });
    media.appendChild(btn);
  };

  // Menú del celular: agrega los accesos por estilo debajo de los enlaces principales
  // (el tema vuelve a pintar el cajón, así que se agregan cada vez que se abre)
  const initMenuStyles = () => {
    const add = () => {
      const nav = document.querySelector('.menu-drawer__navigation');
      if (!nav || nav.querySelector('.ec-menu-styles')) return;
      const clone = (id) => document.getElementById(id)?.content.cloneNode(true);
      const search = clone('ec-menu-search');
      if (search) nav.prepend(search);
      const styles = clone('ec-menu-styles');
      if (styles) nav.appendChild(styles);
      const plates = clone('ec-menu-plates');
      if (plates) nav.appendChild(plates);
    };
    add();
    document.addEventListener('toggle', (e) => {
      if (e.target.matches && e.target.matches('details.menu-drawer-container') && e.target.open) add();
    }, true);
    document.addEventListener('click', (e) => {
      if (e.target.closest && e.target.closest('.header__icon--menu')) requestAnimationFrame(add);
    });
  };

  // Portada: cambia de placa cada pocos segundos (se detiene si la pestaña no se ve o con movimiento reducido)
  const initPortada = () => {
    document.querySelectorAll('[data-ec-portada]').forEach((sec) => {
      if (sec.dataset.ecReady) return;
      sec.dataset.ecReady = '1';
      const slides = [...sec.querySelectorAll('[data-ec-portada-slide]')];
      const now = sec.querySelector('[data-ec-portada-now]');
      const dots = [...sec.querySelectorAll('[data-ec-portada-dot]')];
      const stage = sec.querySelector('.ec-portada__slides');
      if (slides.length < 2) return;
      const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const wait = +sec.dataset.interval || 3000;
      let i = 0;
      let timer = 0;
      const visible = () => {
        const r = sec.getBoundingClientRect();
        return !document.hidden && r.bottom > 0 && r.top < innerHeight;
      };
      const schedule = () => {
        clearTimeout(timer);
        if (still) return;
        timer = setTimeout(() => {
          if (visible()) show((i + 1) % slides.length);
          else schedule();
        }, wait);
      };
      const show = (k) => {
        slides[i].classList.remove('is-active');
        dots[i]?.classList.remove('is-active');
        dots[i]?.removeAttribute('aria-current');
        i = (k + slides.length) % slides.length;
        slides[i].classList.add('is-active');
        // reinicia la barra de progreso de la placa nueva
        if (dots[i]) {
          void dots[i].offsetWidth;
          dots[i].classList.add('is-active');
          dots[i].setAttribute('aria-current', 'true');
        }
        schedule();
        if (!now) return;
        now.classList.add('is-swapping');
        setTimeout(() => {
          now.href = slides[i].dataset.url;
          now.querySelector('[data-ec-portada-name]').textContent = slides[i].dataset.name;
          now.querySelector('[data-ec-portada-price]').textContent = slides[i].dataset.price;
          now.classList.remove('is-swapping');
        }, 300);
      };
      dots.forEach((d) => d.addEventListener('click', () => show(+d.dataset.ecPortadaDot)));
      // Deslizar con el dedo: izquierda = siguiente, derecha = anterior
      let x0 = null;
      stage.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') x0 = e.clientX; });
      stage.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0;
        x0 = null;
        if (Math.abs(dx) > 40) show(i + (dx < 0 ? 1 : -1));
      });
      stage.addEventListener('pointercancel', () => { x0 = null; });
      schedule();
    });
  };

  // Inicio: "Las favoritas" no muestra gorras agotadas (y deja 9)
  const hideSoldOut = () => {
    document.querySelectorAll('.shopify-section[id*="product_list"]').forEach((sec) => {
      const items = [...sec.querySelectorAll('product-card, .product-card')]
        .map((c) => c.closest('.resource-list__item, li') || c.parentElement)
        .filter((v, k, a) => v && a.indexOf(v) === k);
      let shown = 0;
      items.forEach((li) => {
        const soldOut = /agotad|sold out/i.test(li.querySelector('.product-badges')?.textContent || '');
        const keep = !soldOut && shown < 9;
        li.hidden = !keep;
        if (keep) shown++;
      });
    });
  };

  // Carrito: "Agrega otra placa" con las mismas tarjetas del menú (el tema vuelve a pintar el carrito al cambiarlo)
  const initCartMore = () => {
    const drawer = document.querySelector('cart-drawer-component');
    const tpl = document.getElementById('ec-menu-plates');
    if (!drawer || !tpl) return;
    const add = () => {
      const wrap = drawer.querySelector('.cart-items__wrapper');
      if (!wrap || wrap.querySelector('.ec-cart-more') || !drawer.querySelector('.cart-items__table')) return;
      const box = document.createElement('div');
      box.className = 'ec-cart-more';
      box.innerHTML = '<p class="ec-cart-more__title"></p>';
      box.firstChild.textContent = drawer.dataset.moreLabel || tpl.dataset.moreLabel || '';
      box.appendChild(tpl.content.cloneNode(true));
      wrap.appendChild(box);
    };
    add();
    new MutationObserver(add).observe(drawer, { childList: true, subtree: true });
  };

  // Compra rápida en tarjetas: colores (puntos) + "Agregar" directo al carrito, sin entrar a la ficha
  const COLORS = {
    black: '#111', negro: '#111', negra: '#111', 'full black': '#111',
    red: '#c4161c', rojo: '#c4161c', roja: '#c4161c',
    white: '#f4f4f4', blanco: '#f4f4f4', blanca: '#f4f4f4',
    khaki: '#c3b091', kaki: '#c3b091', beige: '#d8c8a8',
    navy: '#1f2a44', blue: '#1f5fbf', azul: '#1f5fbf',
    green: '#2f5d3a', verde: '#2f5d3a', olive: '#6b6b3a',
    caramel: '#a8692e', brown: '#6b4226', cafe: '#6b4226', café: '#6b4226',
    gold: '#c9a33b', dorado: '#c9a33b', silver: '#b8b8b8', plata: '#b8b8b8',
    grey: '#8a8a8a', gray: '#8a8a8a', gris: '#8a8a8a',
    pink: '#e88aa8', rosa: '#e88aa8', orange: '#e8742a', naranja: '#e8742a',
    yellow: '#f2c230', amarillo: '#f2c230', purple: '#6c3fa0', camo: '#5d6b3f',
  };
  // "Black/Red Cotton" -> ['#111', '#c4161c']
  const colorsOf = (name) => {
    const found = [];
    name.toLowerCase().split(/[\/,&+-]| y /).forEach((part) => {
      const p = part.trim();
      const key = Object.keys(COLORS).sort((a, b) => b.length - a.length).find((k) => p === k || p.startsWith(k + ' ') || p.endsWith(' ' + k) || p.includes(' ' + k + ' '));
      if (key) found.push(COLORS[key]);
    });
    return found.slice(0, 2);
  };
  const T = (() => { try { return JSON.parse(document.getElementById('ec-text')?.textContent || '{}'); } catch (_) { return {}; } })();
  const sizedImg = (src, w) => (src ? src.replace(/(\?|&)width=\d+/, '') + (src.includes('?') ? '&' : '?') + 'width=' + w : '');

  const money = (cents) => {
    const fmt = document.getElementById('ec-text') && T.money;
    const n = (cents / 100).toFixed(2);
    return fmt ? fmt.replace(/\{\{\s*amount\s*\}\}/, n) : '$' + n;
  };
  const swapImg = (img, src) => {
    const pre = new Image();
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      img.removeAttribute('srcset');
      img.src = src;
      if (img.animate && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        img.animate([{ opacity: 0.6 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
      }
    };
    pre.onload = pre.onerror = go;
    pre.src = src;
    setTimeout(go, 250);
  };
  // Las fotos de cada color se piden en cuanto el dedo o el mouse llega a los colores
  document.addEventListener('pointerover', (e) => {
    const row = e.target.closest && e.target.closest('.ec-quick__colors');
    if (!row || row.dataset.warm) return;
    row.dataset.warm = '1';
    row.querySelectorAll('[data-img]').forEach((b) => { const i = new Image(); i.src = b.dataset.img; });
  }, { passive: true });

  const addToCart = async (variantId, sourceEl) => {
    const sections = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
    const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sections.join(',') }),
    });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || 'add');
    // mismo aviso que usa el tema: el carrito se repinta, el contador sube y el cajón se abre
    const ev = new Event('cart:update', { bubbles: true });
    ev.detail = { resource: {}, sourceId: 'ec-quick', data: { source: 'ec-quick', itemCount: 1, sections: json.sections } };
    sourceEl.dispatchEvent(ev);
    toast();
  };
  // Aviso pequeño abajo: "Agregada · Ver carrito" (no interrumpe; el contador del carrito ya subió)
  let toastEl;
  let toastTimer = 0;
  const toast = () => {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'ec-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.innerHTML = '<span></span><button type="button"></button>';
      toastEl.querySelector('button').addEventListener('click', () => {
        document.querySelector('cart-drawer-component')?.open?.();
        toastEl.classList.remove('is-in');
      });
      document.body.appendChild(toastEl);
    }
    toastEl.querySelector('span').textContent = T.added || 'Agregada';
    toastEl.querySelector('button').textContent = (T.viewCart || 'Ver carrito') + ' →';
    requestAnimationFrame(() => toastEl.classList.add('is-in'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-in'), 3200);
  };

  const buildQuick = async (card) => {
    const link = card.querySelector('a[href*="/products/"]');
    const handle = link && new URL(link.href, location.origin).pathname.split('/products/')[1]?.split('/')[0];
    if (!handle) return;
    let p;
    try { p = await fetch(`/products/${handle}.js`).then((r) => r.json()); } catch (_) { return; }
    if (!p || !p.variants) return;
    const box = document.createElement('div');
    box.className = 'ec-quick';
    const variants = p.variants;
    let current = variants.find((v) => v.available) || variants[0];
    const img = card.querySelector('.card-gallery img, img');

    if (variants.length > 1) {
      const name = document.createElement('span');
      name.className = 'ec-quick__name';
      name.textContent = current.title;
      const row = document.createElement('div');
      row.className = 'ec-quick__colors';
      row.setAttribute('role', 'radiogroup');
      row.setAttribute('aria-label', T.color || 'Color');
      variants.forEach((v) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ec-quick__swatch';
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-label', v.title + (v.available ? '' : ' — ' + (T.soldOut || 'Agotada')));
        b.title = v.title;
        const cs = colorsOf(v.title);
        if (cs.length) b.style.setProperty('--sw', cs.length > 1 ? `linear-gradient(135deg, ${cs[0]} 50%, ${cs[1]} 50%)` : cs[0]);
        else { b.classList.add('ec-quick__swatch--text'); b.textContent = v.title.split('/')[0].trim().slice(0, 10); }
        if (!v.available) b.classList.add('is-out');
        if (v.featured_image?.src) b.dataset.img = sizedImg(v.featured_image.src, 800);
        const select = () => {
          current = v;
          name.textContent = v.title;
          row.querySelectorAll('.ec-quick__swatch').forEach((x) => x.setAttribute('aria-checked', String(x === b)));
          add.disabled = !v.available;
          add.textContent = v.available ? '+ ' + (T.add || 'Agregar') : (T.soldOut || 'Agotada');
          if (img && v.featured_image?.src) swapImg(img, sizedImg(v.featured_image.src, 800));
          const price = card.querySelector('product-price .price, .price');
          if (price) price.textContent = money(v.price);
          card.querySelectorAll('a[href*="/products/"]').forEach((a) => {
            const u = new URL(a.href, location.origin);
            u.searchParams.set('variant', v.id);
            a.href = u.pathname + u.search;
          });
        };
        b.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); select(); });
        b.setAttribute('aria-checked', String(v === current));
        row.appendChild(b);
      });
      box.appendChild(row);
      box.appendChild(name);
    } else {
      // sin colores: mismo alto que la fila de colores, así los botones de la fila quedan parejos
      const gap = document.createElement('div');
      gap.className = 'ec-quick__spacer';
      gap.setAttribute('aria-hidden', 'true');
      box.appendChild(gap);
    }

    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'ec-quick__add';
    add.disabled = !current.available;
    add.textContent = current.available ? '+ ' + (T.add || 'Agregar') : (T.soldOut || 'Agotada');
    add.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (add.disabled) return;
      add.disabled = true;
      add.classList.add('is-busy');
      try {
        await addToCart(current.id, card);
        add.textContent = (T.added || 'Agregada') + ' ✓';
        add.classList.add('is-done');
      } catch (_) {
        add.textContent = T.error || 'Intenta de nuevo';
      }
      setTimeout(() => {
        add.disabled = !current.available;
        add.classList.remove('is-busy', 'is-done');
        add.textContent = current.available ? '+ ' + (T.add || 'Agregar') : (T.soldOut || 'Agotada');
      }, 1800);
    });
    box.appendChild(add);
    // que el clic en los controles no abra la ficha
    box.addEventListener('click', (e) => e.stopPropagation());
    (card.querySelector('.product-card__content') || card).appendChild(box);
  };

  const initQuick = (root = document) => {
    const cards = [...root.querySelectorAll('product-card, .product-card')].filter((c) => !c.closest('.menu-drawer, .cart-drawer') && !c.dataset.ecQuick);
    if (!cards.length) return;
    const go = (c) => { if (c.dataset.ecQuick) return; c.dataset.ecQuick = '1'; buildQuick(c); };
    if (!('IntersectionObserver' in window)) return cards.forEach(go);
    const qio = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { go(e.target); qio.unobserve(e.target); } });
    }, { rootMargin: '200px 0px' });
    cards.forEach((c) => qio.observe(c));
  };

  // Favoritos: estrella en cada tarjeta, botón en la ficha y panel "Mis favoritas" desde el header.
  // Se guardan en el navegador del cliente (sin cuenta). Los datos de cada gorra se piden a /products/<handle>.js.
  const FKEY = 'ec-favs';
  const readFavs = () => { try { return JSON.parse(localStorage.getItem(FKEY) || '[]'); } catch (_) { return []; } };
  const writeFavs = (list) => { try { localStorage.setItem(FKEY, JSON.stringify(list)); } catch (_) {} };
  const isFav = (h) => readFavs().includes(h);
  const handleOf = (el) => {
    const a = el.querySelector('a[href*="/products/"]');
    return a ? new URL(a.href, location.origin).pathname.split('/products/')[1]?.split('/')[0] : null;
  };
  const STAR = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.8l2.7 6 6.5.6-4.9 4.3 1.5 6.4L12 16.8l-5.8 3.3 1.5-6.4L2.8 9.4l6.5-.6z"/></svg>';
  const syncStars = () => {
    const favs = readFavs();
    document.querySelectorAll('[data-ec-fav]').forEach((b) => {
      const on = favs.includes(b.dataset.ecFav);
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', String(on));
      if (b.classList.contains('ec-fav-pdp')) b.querySelector('span').textContent = on ? (T.favSaved || 'En tus favoritas') : (T.favSave || 'Guardar en favoritas');
    });
    const count = document.querySelector('[data-ec-favs-count]');
    if (count) {
      count.textContent = favs.length;
      count.closest('.ec-favs-open').classList.toggle('has-items', favs.length > 0);
    }
  };
  const toggleFav = (h, btn) => {
    let favs = readFavs();
    favs = favs.includes(h) ? favs.filter((x) => x !== h) : [h, ...favs].slice(0, 40);
    writeFavs(favs);
    syncStars();
    if (btn && btn.animate && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      btn.animate([{ transform: 'scale(0.8)' }, { transform: 'scale(1.15)' }, { transform: 'scale(1)' }], { duration: 320, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' });
    }
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest && e.target.closest('[data-ec-fav]');
    if (!b) return;
    e.preventDefault();
    e.stopPropagation();
    toggleFav(b.dataset.ecFav, b.classList.contains('ec-fav-pdp') ? b.querySelector('svg') : b);
  }, true);
  window.addEventListener('storage', (e) => { if (e.key === FKEY) syncStars(); });

  const addStar = (card) => {
    if (card.dataset.ecStar || card.closest('.menu-drawer, .cart-drawer, .ec-favs')) return;
    const h = handleOf(card);
    const gallery = card.querySelector('.card-gallery');
    if (!h || !gallery) return;
    card.dataset.ecStar = '1';
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'ec-fav';
    b.dataset.ecFav = h;
    b.setAttribute('aria-label', T.favSave || 'Guardar en favoritas');
    b.innerHTML = STAR;
    gallery.appendChild(b);
  };

  // Panel "Mis favoritas"
  let panel;
  const money2 = (c) => money(c);
  const renderPanel = async () => {
    const list = panel.querySelector('.ec-favs__list');
    const favs = readFavs();
    panel.querySelector('.ec-favs__empty').hidden = favs.length > 0;
    list.innerHTML = '';
    const items = await Promise.all(favs.map((h) => fetch(`/products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)));
    items.forEach((p, k) => {
      if (!p) return;
      const v = p.variants.find((x) => x.available) || p.variants[0];
      const li = document.createElement('li');
      li.className = 'ec-favs__item';
      li.innerHTML = `<a class="ec-favs__img" href="${p.url}"><img alt="" loading="lazy"></a>
        <div class="ec-favs__info"><a class="ec-favs__name" href="${p.url}"></a><span class="ec-favs__price"></span>
        <div class="ec-favs__actions"><button type="button" class="ec-quick__add"></button>
        <button type="button" class="ec-favs__remove"></button></div></div>`;
      li.querySelector('img').src = sizedImg(p.featured_image, 300);
      li.querySelector('.ec-favs__name').textContent = p.title;
      li.querySelector('.ec-favs__price').textContent = money2(v.price);
      const add = li.querySelector('.ec-quick__add');
      add.disabled = !v.available;
      add.textContent = v.available ? '+ ' + (T.add || 'Agregar') : (T.soldOut || 'Agotada');
      add.addEventListener('click', async () => {
        add.disabled = true;
        try { await addToCart(v.id, panel); add.textContent = (T.added || 'Agregada') + ' ✓'; } catch (_) { add.textContent = T.error || 'Intenta de nuevo'; }
        setTimeout(() => { add.disabled = false; add.textContent = '+ ' + (T.add || 'Agregar'); }, 1800);
      });
      const rm = li.querySelector('.ec-favs__remove');
      rm.textContent = T.favRemove || 'Quitar';
      rm.addEventListener('click', () => { toggleFav(favs[k]); li.remove(); panel.querySelector('.ec-favs__empty').hidden = readFavs().length > 0; });
      list.appendChild(li);
    });
  };
  const openPanel = () => {
    if (!panel) {
      panel = document.createElement('dialog');
      panel.className = 'ec-favs';
      panel.innerHTML = `<div class="ec-favs__head"><p class="ec-favs__title"></p><button type="button" class="ec-favs__close"></button></div>
        <p class="ec-favs__empty"></p><ul class="ec-favs__list"></ul>`;
      panel.querySelector('.ec-favs__title').textContent = T.favTitle || 'Mis favoritas';
      panel.querySelector('.ec-favs__empty').textContent = T.favEmpty || 'Toca la estrella de una gorra para guardarla aquí.';
      const close = panel.querySelector('.ec-favs__close');
      close.setAttribute('aria-label', T.close || 'Cerrar');
      close.textContent = '×';
      close.addEventListener('click', () => panel.close());
      panel.addEventListener('click', (e) => { if (e.target === panel) panel.close(); });
      document.body.appendChild(panel);
    }
    renderPanel();
    panel.showModal();
  };
  const addHeaderStar = () => {
    if (document.querySelector('.ec-favs-open')) return;
    const cartBtn = document.querySelector('.header-actions__action[aria-label*="arrito"], .header-actions__action[aria-label*="cart" i]');
    const host = cartBtn?.closest('cart-drawer-component') || cartBtn;
    if (!host) return;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'ec-favs-open';
    b.setAttribute('aria-label', T.favTitle || 'Mis favoritas');
    b.innerHTML = STAR + '<span data-ec-favs-count>0</span>';
    b.addEventListener('click', openPanel);
    host.parentElement.insertBefore(b, host);
  };
  const addPdpFav = () => {
    const form = document.querySelector('main form[data-type="add-to-cart-form"]');
    const h = document.querySelector('main [data-product-handle]')?.dataset.productHandle || (location.pathname.split('/products/')[1] || '').split('/')[0];
    if (!form || !h || document.querySelector('.ec-fav-pdp')) return;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'ec-fav-pdp';
    b.dataset.ecFav = h;
    b.innerHTML = STAR + '<span></span>';
    (form.closest('.buy-buttons-block, product-form-component') || form).insertAdjacentElement('afterend', b);
  };
  const initFavs = (root = document) => {
    root.querySelectorAll('product-card, .product-card').forEach(addStar);
    // el tema vuelve a pintar el header y la ficha después de cargar: se reintenta en cada cambio
    addHeaderStar();
    addPdpFav();
    syncStars();
  };

  // Barra de compra en celular: aparece cuando el botón "Agregar al carrito" sale de la pantalla
  const initBuybar = () => {
    const bar = document.querySelector('[data-ec-buybar]');
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    const real = form && form.querySelector('[type="submit"]');
    if (!bar || !real || !('IntersectionObserver' in window)) return;
    const btn = bar.querySelector('[data-ec-buybar-btn]');
    const price = bar.querySelector('[data-ec-buybar-price]');
    bar.hidden = false;
    let realVisible = true;
    let realAbove = false;
    const sync = () => {
      const show = !realVisible && realAbove;
      bar.classList.toggle('is-in', show);
      bar.inert = !show;
      // Mismo estado y precio que el botón real (variante elegida, agotado)
      btn.disabled = real.disabled;
      btn.textContent = real.disabled ? btn.dataset.soldOut : btn.dataset.add;
      const p = document.querySelector('.product-details product-price, product-price');
      if (p && p.textContent.trim()) price.textContent = p.textContent.trim().split(/\s{2,}/)[0];
    };
    new IntersectionObserver(([e]) => {
      realVisible = e.isIntersecting;
      realAbove = e.boundingClientRect.top < 0;
      sync();
    }).observe(real);
    btn.addEventListener('click', () => real.click());
    sync();
  };

  const start = () => {
    initStyles();
    initFlip();
    initLupa();
    initBuybar();
    initTilt();
    initLupaJump();
    initMenuStyles();
    initPortada();
    hideSoldOut();
    initCartMore();
    initQuick();
    initFavs();
    window.addEventListener('load', () => initFavs());
    // El hero está arriba del pliegue: entra al cargar, sin esperar al scroll
    document.querySelectorAll('.ec-hero').forEach((h) => {
      seen.add(h);
      requestAnimationFrame(() => h.classList.add('is-in'));
    });
    scan();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // Filtros / paginación / editor de temas cargan HTML nuevo
  new MutationObserver((muts) => {
    for (const m of muts) for (const n of m.addedNodes) if (n.nodeType === 1) { scan(n); initQuick(n); if (n.querySelectorAll) initFavs(n); }
  }).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', (e) => { scan(e.target); initStyles(e.target); initFlip(e.target); initLupa(e.target); });
})();
